import mixpanel from "mixpanel-browser";
import { TelemetryCompany, TelemetryAccount } from "@repo/types/index";

const MIXPANEL_TOKEN = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN;

export const initMixpanel = (): boolean => {
  if (!MIXPANEL_TOKEN) {
    console.warn("Mixpanel token is missing! Check your .env file.");
    return false;
  }

  try {
    mixpanel.init(MIXPANEL_TOKEN, {
      autocapture: true,
    });

    mixpanel.register({ "App ID": "client-portal-modern" });
    return true;
  } catch (error) {
    console.error("Failed to initialize Mixpanel:", error);
    return false;
  }
};

export const trackPageView = (url: string) => {
  const initialized = initMixpanel();
  if (!initialized) return;

  mixpanel.track("Page View", {
    url,
    title: document.title,
    referrer: document.referrer || "direct",
  });
};

export const track = async (
  eventName: string,
  properties?: Record<string, any>,
) => {
  const initialized = await initMixpanel();
  if (!initialized) {
    console.log("Skipping track, not initialized:", eventName);
    return;
  }

  try {
    mixpanel.track(eventName, {
      ...properties,
      timestamp: new Date().toISOString(),
      environment: process.env.NODE_ENV,
    });
  } catch (error) {
    console.error(`Failed to track ${eventName}:`, error);
  }
};

type TelemetryData = {
  company: TelemetryCompany;
  account: TelemetryAccount;
};

export const trackCustomer = async ({ company, account }: TelemetryData) => {
  const initialized = await initMixpanel();
  if (!initialized) return;

  try {
    mixpanel.identify(account.id);
    mixpanel.people.set({
      $name: account.fullName,
      $email: account.email,
      company_id: company.id,
      company_name: company.name,
    });
  } catch (error) {
    console.error("Failed to track customer:", error);
  }
};

type BusinessObject = "Estimate" | "Invoice" | "Payment";
type EventObject =
  | "Accept Modal"
  | "Decline Modal"
  | "Changes Requested Modal"
  | "PDF Download"
  | "Line Item Toggle"
  | "Pay In Full"
  | "Contact"
  | "History"
  | string;
type Action = "Opened" | "Submitted" | "Clicked" | "Success" | "Closed";

export function mixpanelLabel(
  businessObject: BusinessObject,
  eventObject: EventObject,
  action: Action,
) {
  return `${businessObject} / ${eventObject} / ${action}`;
}
